import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchVendors,
  createVendor,
  updateVendor,
  deleteVendor,
  clearError,
  exportVendors,
  importPreview,
  importConfirm,
} from 'store/slices/vendorSlice';
import { Pencil, Trash2, MoreVertical, Loader2 } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import PageHeader from 'components/common/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { restoreRole } from 'store/slices/commonSlice';
import { formatDateTime } from 'utlis/formateDateTime';

const VendorPage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();

  const { vendors, pagination, isLoading, error } = useSelector(
    (state) => state.vendor
  );

  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } =
    usePermissions('vendor');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    phone_number: '',
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState({});

  const refetch = useCallback(() => {
    dispatch(fetchVendors({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const openDialog = (vendor = null) => {
    if (vendor) {
      if (!canUpdate) return toast.error('No permission to edit');
      setCurrentVendor(vendor);
      setFormData({
        name: vendor.name,
        code: vendor.code,
        phone_number: vendor.phone_number || '',
        is_active: vendor.is_active,
      });
    } else {
      if (!canCreate) return toast.error('No permission to create');
      setCurrentVendor(null);
      setFormData({
        name: '',
        code: '',
        phone_number: '',
        is_active: true,
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Vendor name is required';
    if (!formData.code.trim()) errors.code = 'Vendor code is required';
    if (!formData.phone_number.trim())
      errors.phone_number = 'Phone number is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (currentVendor) {
        await dispatch(
          updateVendor({ id: currentVendor.id, ...formData })
        ).unwrap();
        toast.success('Vendor updated successfully');
      } else {
        await dispatch(createVendor(formData)).unwrap();
        toast.success('Vendor created successfully');
      }
      setDialogOpen(false);
      refetch();
    } catch { }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error('No permission');
    try {
      await dispatch(deleteVendor(currentVendor.id)).unwrap();
      toast.success('Vendor deleted');
      setDeleteOpen(false);
      refetch();
    } catch { }
  };

  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error('No import permission');
    return await dispatch(importPreview(file)).unwrap();
  };

  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error('No import permission');
    await dispatch(importConfirm(preview)).unwrap();
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
    },
    {
      accessorKey: 'phone_number',
      header: 'Phone',
      cell: ({ row }) => (
        row.original.phone_number ?? '-'
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
       {
          accessorKey: 'created_at',
          header: 'Created On',
          cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
        },
        {
          accessorKey: 'created_by_name',
          header: 'Created By',
          cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
        },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const vendor = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onClick={() => openDialog(vendor)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentVendor(vendor);
                    setDeleteOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Vendors"
        description="Manage vendors in the system"
        action={
          canCreate
            ? { label: 'Add Vendor', onClick: () => openDialog() }
            : null
        }
      />
{/* 
      {isLoading && !vendors.length && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading vendors...
        </div>
      )} */}

      {canRead ? (
        <DataTable
          data={vendors}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/vendor.csv"
          templateDownloadName="vendor-import-template.csv"
          templateKey="vendor"
          searchPlaceholder="Search vendors..."
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={Math.ceil(pagination.count / pagination.page_size)}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportVendors()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? handleImportPreview : undefined}
          onImportConfirm={canImport ? handleImportConfirm : undefined}
          onDelete={async (id) => {
            await dispatch(deleteVendor(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'vendor', id }));
            refetch();
          }}
        />
      ) : (
        <p className="text-center text-muted-foreground">
          You don't have permission to view vendors.
        </p>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {currentVendor ? 'Edit Vendor' : 'Add Vendor'}
            </DialogTitle>
            <DialogDescription>
              {currentVendor
                ? 'Update vendor details'
                : 'Create a new vendor'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {formErrors.name && (
                <p className="text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            <div>
              <Label>Vendor Code</Label>
              <Input
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
              />
              {formErrors.code && (
                <p className="text-sm text-red-600">{formErrors.code}</p>
              )}
            </div>

            <div>
              <Label>Phone Number</Label>
              <Input
                value={formData.phone_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    phone_number: e.target.value,
                  })
                }
              />
              {formErrors.phone_number && (
                <p className="text-sm text-red-600">
                  {formErrors.phone_number}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
              />
              <Label>Active</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {currentVendor ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete{' '}
              <strong>{currentVendor?.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorPage;
